import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Layout, Menu, Dropdown, message, Spin, theme } from 'antd';
import type { MenuProps } from 'antd';
import {
  DashboardOutlined,
  VideoCameraOutlined,
  AppstoreOutlined,
  UserOutlined,
  FileTextOutlined,
  StarOutlined,
  CloudDownloadOutlined,
  NotificationOutlined,
  CommentOutlined,
  MessageOutlined,
  SettingOutlined,
  CustomerServiceOutlined,
  LinkOutlined,
  LogoutOutlined,
  PlusOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import { logoutApi } from '@/api';
import { ADMIN_KEY, TOKEN_KEY } from '@/api/client';
import type { AdminInfo } from '@/types';

const { Header, Sider, Content } = Layout;

// 侧边栏菜单配置
const menuItems: MenuProps['items'] = [
  { key: '/dashboard', icon: <DashboardOutlined />, label: '仪表盘' },
  {
    key: '/vod',
    icon: <VideoCameraOutlined />,
    label: '视频管理',
    children: [
      { key: '/vod/list', icon: <VideoCameraOutlined />, label: '视频列表' },
      { key: '/vod/create', icon: <PlusOutlined />, label: '新建视频' },
      { key: '/vod/filter', icon: <FilterOutlined />, label: '视频过滤' },
    ],
  },
  { key: '/type/list', icon: <AppstoreOutlined />, label: '分类管理' },
  { key: '/user/list', icon: <UserOutlined />, label: '用户管理' },
  { key: '/art/list', icon: <FileTextOutlined />, label: '文章管理' },
  { key: '/topic/list', icon: <StarOutlined />, label: '专题管理' },
  { key: '/collect/list', icon: <CloudDownloadOutlined />, label: '采集管理' },
  { key: '/ad/list', icon: <NotificationOutlined />, label: '广告管理' },
  { key: '/comment/list', icon: <CommentOutlined />, label: '评论管理' },
  { key: '/gbook/list', icon: <MessageOutlined />, label: '留言管理' },
  { key: '/player/list', icon: <CustomerServiceOutlined />, label: '播放器管理' },
  { key: '/link/list', icon: <LinkOutlined />, label: '友情链接' },
  { key: '/system/config', icon: <SettingOutlined />, label: '系统设置' },
];

// 页面标题映射
const pageTitleMap: Record<string, string> = {
  '/dashboard': '仪表盘',
  '/vod/list': '视频列表',
  '/vod/create': '新建视频',
  '/vod/filter': '视频过滤',
  '/type/list': '分类管理',
  '/user/list': '用户管理',
  '/art/list': '文章管理',
  '/topic/list': '专题管理',
  '/collect/list': '采集管理',
  '/ad/list': '广告管理',
  '/comment/list': '评论管理',
  '/gbook/list': '留言管理',
  '/player/list': '播放器管理',
  '/link/list': '友情链接',
  '/system/config': '系统设置',
};

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const [adminInfo] = useState<AdminInfo | null>(() => {
    try {
      const raw = localStorage.getItem(ADMIN_KEY);
      return raw ? (JSON.parse(raw) as AdminInfo) : null;
    } catch {
      return null;
    }
  });

  // 退出登录
  const handleLogout = async () => {
    try {
      await logoutApi();
    } catch {
      // 忽略服务端错误
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ADMIN_KEY);
    message.success('已退出登录');
    navigate('/login', { replace: true });
  };

  // 右上角下拉菜单
  const dropdownItems: MenuProps['items'] = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout,
    },
  ];

  // 菜单点击导航
  const onMenuClick: MenuProps['onClick'] = ({ key }) => {
    navigate(key);
  };

  // 计算当前展开的菜单和选中的项
  const selectedKeys = [location.pathname];
  const openKeys = ['/vod']; // 视频管理默认展开

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        trigger={null}
        width={220}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
        }}
      >
        <div
          style={{
            height: 48,
            margin: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: collapsed ? 16 : 20,
            fontWeight: 700,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
        >
          {collapsed ? '悟空' : '悟空影视后台'}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={selectedKeys}
          defaultOpenKeys={openKeys}
          items={menuItems}
          onClick={onMenuClick}
        />
      </Sider>
      <Layout style={{ marginLeft: collapsed ? 80 : 220, transition: 'margin-left 0.2s' }}>
        <Header
          style={{
            padding: '0 24px',
            background: colorBgContainer,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span
              onClick={() => setCollapsed(!collapsed)}
              style={{ fontSize: 18, cursor: 'pointer', padding: '0 4px' }}
            >
              {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            </span>
            <span style={{ fontSize: 16, fontWeight: 600 }}>
              {pageTitleMap[location.pathname] || '悟空影视后台'}
            </span>
          </div>
          <Dropdown menu={{ items: dropdownItems }} placement="bottomRight">
            <a style={{ display: 'flex', alignItems: 'center', gap: 6 }} onClick={(e) => e.preventDefault()}>
              <UserOutlined />
              <span>{adminInfo?.admin_name || '管理员'}</span>
            </a>
          </Dropdown>
        </Header>
        <Content style={{
          margin: 16,
          padding: 24,
          background: colorBgContainer,
          minHeight: 280,
        }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}

/** 内容区加载骨架，供页面切换时使用 */
export function PageLoading() {
  return (
    <div style={{ padding: 48, textAlign: 'center' }}>
      <Spin />
    </div>
  );
}
